import React, { useState } from 'react';
import { FlagIcon } from '@heroicons/react/24/outline';
import { ReportedResource } from '../../types/report';
import { ReportModal } from './ReportModal';

interface ReportButtonProps {
    reportedResource: ReportedResource;
    className?: string;
    size?: 'sm' | 'md' | 'lg';
    variant?: 'button' | 'text' | 'icon';
    label?: string;
    onReportSubmitted?: (success: boolean) => void;
}

export const ReportButton: React.FC<ReportButtonProps> = ({
    reportedResource,
    className = '',
    size = 'md',
    variant = 'button',
    label,
    onReportSubmitted
}) => {
    const [showReportModal, setShowReportModal] = useState(false);

    const sizeClasses = {
        sm: 'px-2 py-1 text-xs',
        md: 'px-3 py-2 text-sm',
        lg: 'px-4 py-2 text-base'
    };

    const iconSizes = {
        sm: 'h-3 w-3',
        md: 'h-4 w-4',
        lg: 'h-5 w-5'
    };

    const handleReportSubmitted = (success: boolean) => {
        setShowReportModal(false);
        onReportSubmitted?.(success);
    };

    const renderButton = () => {
        const iconClass = iconSizes[size];

        switch (variant) {
            case 'text':
                return (
                    <button
                        onClick={() => setShowReportModal(true)}
                        className={`inline-flex items-center text-gray-500 hover:text-red-500 dark:text-gray-400 dark:hover:text-red-400 transition-colors ${sizeClasses[size]} ${className}`}
                        aria-label="Report this content"
                    >
                        <FlagIcon className={`${iconClass} mr-1`} />
                        {label || 'Report'}
                    </button>
                );

            case 'icon':
                return (
                    <button
                        onClick={() => setShowReportModal(true)}
                        className={`inline-flex items-center justify-center text-gray-500 hover:text-red-500 dark:text-gray-400 dark:hover:text-red-400 hover:bg-gray-100 dark:hover:bg-gray-700 rounded-full p-2 transition-colors ${className}`}
                        aria-label="Report this content"
                    >
                        <FlagIcon className={iconClass} />
                    </button>
                );

            default: // button
                return (
                    <button
                        onClick={() => setShowReportModal(true)}
                        className={`inline-flex items-center justify-center border border-gray-300 dark:border-gray-600 rounded-md font-medium text-gray-700 dark:text-gray-300 bg-white dark:bg-gray-700 hover:bg-gray-50 dark:hover:bg-gray-600 hover:text-red-600 dark:hover:text-red-400 hover:border-red-300 dark:hover:border-red-600 transition-colors focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-red-500 ${sizeClasses[size]} ${className}`}
                    >
                        <FlagIcon className={`${iconClass} mr-1`} />
                        {label || 'Report'}
                    </button>
                );
        }
    };

    return (
        <>
            {renderButton()}

            <ReportModal
                isOpen={showReportModal}
                onClose={() => setShowReportModal(false)}
                reportedResource={reportedResource}
                onReportSubmitted={handleReportSubmitted}
            />
        </>
    );
};